import Cookies from "js-cookie";
import { CheckoutPlan, decorateUrlWithUTMParams } from "../LandingPage";
import { getInternalCoreEndpoint } from "../utils";
import { sha256 } from "js-sha256";
import { User } from "../types";
import { LOCAL_STORAGE_SESSION_IDENTITY_SESSION_ID_KEY } from "./useAnswerTracker";

// web29_trial_toggle: the no-trial path applies a 20%-off promotion code that the
// backend recognizes and charges immediately (no trial period). These are
// dedicated codes for this funnel (Stripe coupons O7fJsFXI / QGvwXquj):
//   NOTRIAL20M (monthly/serious)  -> 20% off, repeating for the first 12 months
//   NOTRIAL20A (annual/committed) -> 20% off, once (the first year's invoice)
export function getPlanPromotionCode(plan?: CheckoutPlan) {
  if (plan?.promotionCode) return plan.promotionCode;
  if (plan?.id === "serious") return "NOTRIAL20M";
  if (plan?.id === "committed") return "NOTRIAL20A";
  return undefined;
}

export function createGetClientSecretPromise(
  plan?: CheckoutPlan,
  eventID?: string,
  user?: User,
  isWeb17Variation?: boolean,
  isNoTrialVariation = false
) {
  const mode = "web";
  const subscriptionPriceId = plan?.priceId ?? "";
  const unlockProductId = plan?.unlockProductId ?? "";
  const trialProductId = plan?.trialProductId ?? "";

  const amplitudeId = Cookies.get("bws_amplitude_device_id");
  const sessionIdentitySessionId = localStorage.getItem(
      LOCAL_STORAGE_SESSION_IDENTITY_SESSION_ID_KEY
  );

  let urlObj: URL;
  const web17_3DiscountId = plan?.experiments?.web17_3?.discount;
  if (isNoTrialVariation) {
    // No trial: subscription charged today with a one-time 20%-off promo code.
    // The code is duration:once, so the discount applies to the first payment
    // only (i.e. just the first year for an annual plan) and renews at full price.
    urlObj = new URL(
      `${getInternalCoreEndpoint()}/internal/public/checkout/${mode}/create-subscription/${subscriptionPriceId}/${unlockProductId}`
    );
    urlObj.searchParams.set("pricing_variant", "no_trial");
    const promotionCode = getPlanPromotionCode(plan);
    if (promotionCode) {
      urlObj.searchParams.set("promotion_code", promotionCode);
    }
  } else if (isWeb17Variation && web17_3DiscountId) {
    urlObj = new URL(
      `${getInternalCoreEndpoint()}/internal/public/checkout/${mode}/create-discount/${subscriptionPriceId}/${unlockProductId}/${trialProductId}/${web17_3DiscountId}`
    );
  } else {
    urlObj = new URL(
      `${getInternalCoreEndpoint()}/internal/public/checkout/${mode}/create-trial/${subscriptionPriceId}/${unlockProductId}/${trialProductId}`
    );
  }

  urlObj.searchParams.set("ui_mode", "custom");
  urlObj.searchParams.set("tos_shown", "false");

  const emailToUse = (user?.email || "").trim();
  if (emailToUse) {
    urlObj.searchParams.set("email", sha256(emailToUse)); // Hashed for tracking
    urlObj.searchParams.set("prefillEmail", emailToUse); // Actual email for customer_email
  }

  if (user?.name) {
    urlObj.searchParams.set("name", user.name);
  }

  if (amplitudeId) {
    urlObj.searchParams.set("amplitudeId", amplitudeId);
  }

  if (eventID) {
      urlObj.searchParams.set("fb_event_id", eventID);
  }

  if (sessionIdentitySessionId) {
      urlObj.searchParams.set(
          "session_identity_session_id",
          sessionIdentitySessionId
      );
  }

  decorateUrlWithUTMParams(urlObj);

  return async () => {
    const res = await fetch(urlObj.toString(), {
      method: "GET",
    });
    const data = await res.json();
    return data.client_secret;
  };
}
